import React, { useEffect, useState, useCallback } from 'react';
import { MapPin, Sparkles, Loader2, Lightbulb } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import {
  warehouseTransferService,
  ItemLocationInfo,
  PutAwayLocationCandidate
} from '../../../../../services/warehouseTransferService';

interface ToLocationSuggestionsProps {
  itemNo: string;
  fromLocationName: string;
  /** Lager forslagene skal gjelde for (til-lager). */
  toWarehouse: string;
  /** Kalles når operatøren velger et forslag. */
  onSelect: (binCode: string, warehouseID: string) => void;
}

/**
 * Forslag til til-lokasjon i lokasjonsflytt.
 * - Lett variant (auto): lokasjoner som allerede har saldo for varen.
 * - Tung variant (knapp): put-away-algoritmen for gode plasserings-kandidater.
 *
 * Klikk på et forslag fyller til-lokasjon i parent (read-only oppslag — ingen reservasjon).
 */
export const ToLocationSuggestions: React.FC<ToLocationSuggestionsProps> = ({
  itemNo,
  fromLocationName,
  toWarehouse,
  onSelect
}) => {
  const { t } = useTranslation();

  const [balanceLocations, setBalanceLocations] = useState<ItemLocationInfo[]>([]);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const [putAwayCandidates, setPutAwayCandidates] = useState<PutAwayLocationCandidate[] | null>(null);
  const [loadingPutAway, setLoadingPutAway] = useState(false);

  // Lett variant: last saldo-lokasjoner automatisk når vare/fra-lokasjon endres
  useEffect(() => {
    let cancelled = false;
    if (!itemNo || !fromLocationName) {
      setBalanceLocations([]);
      return;
    }

    const load = async () => {
      setLoadingBalance(true);
      try {
        const locations = await warehouseTransferService.getLocationsForItem(itemNo);
        if (cancelled) return;
        const filtered = locations
          .filter(loc => loc.locationName !== fromLocationName)
          .filter(loc => !toWarehouse || String(loc.warehouseID) === String(toWarehouse))
          .sort((a, b) => {
            // Primær/fast bin først, deretter mest tilgjengelig saldo
            const aPriority = (a.binCodePrimary ? 2 : 0) + (a.binCodeFixed ? 1 : 0);
            const bPriority = (b.binCodePrimary ? 2 : 0) + (b.binCodeFixed ? 1 : 0);
            if (bPriority !== aPriority) return bPriority - aPriority;
            return b.availableQuantity - a.availableQuantity;
          });
        setBalanceLocations(filtered);
      } catch {
        if (!cancelled) setBalanceLocations([]);
      } finally {
        if (!cancelled) setLoadingBalance(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [itemNo, fromLocationName, toWarehouse]);

  // Nullstill put-away-forslag når kontekst endres (operatøren må kjøre på nytt)
  useEffect(() => {
    setPutAwayCandidates(null);
  }, [itemNo, fromLocationName, toWarehouse]);

  const runPutAway = useCallback(async () => {
    if (!itemNo || !toWarehouse) return;
    setLoadingPutAway(true);
    try {
      const candidates = await warehouseTransferService.getPutAwayLocationCandidates(itemNo, toWarehouse);
      const filtered = candidates
        .filter(c => c.binCode && c.binCode !== fromLocationName)
        .sort((a, b) => a.putAwayPriorityNo - b.putAwayPriorityNo);
      setPutAwayCandidates(filtered);
    } catch {
      setPutAwayCandidates([]);
    } finally {
      setLoadingPutAway(false);
    }
  }, [itemNo, toWarehouse, fromLocationName]);

  return (
    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
      <h2 className="font-semibold mb-3 flex items-center text-sm sm:text-base">
        <Lightbulb className="h-5 w-5 mr-2 text-amber-500" />
        {t('warehouse.modules.warehouseTransfer.suggestions.title', {}, 'Forslag til til-lokasjon')}
      </h2>

      {/* Lett variant: saldo-lokasjoner */}
      <div className="mb-2 flex items-center text-xs font-medium text-gray-500 uppercase tracking-wide">
        <MapPin className="h-3.5 w-3.5 mr-1.5 text-cyan-500" />
        {t('warehouse.modules.warehouseTransfer.suggestions.withBalance', {}, 'Lokasjoner med saldo')}
      </div>

      {loadingBalance ? (
        <div className="flex items-center text-sm text-gray-500 py-2">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          {t('common.status.loading', {}, 'Laster...')}
        </div>
      ) : balanceLocations.length === 0 ? (
        <p className="text-sm text-gray-400 py-1">
          {t('warehouse.modules.warehouseTransfer.suggestions.noBalance', {}, 'Ingen andre lokasjoner med saldo for varen.')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {balanceLocations.map(loc => (
            <button
              key={`${loc.locationName}|${loc.warehouseID}`}
              onClick={() => onSelect(loc.locationName, String(loc.warehouseID))}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-cyan-200 bg-cyan-50 hover:bg-cyan-100 text-cyan-800 transition-colors text-sm min-h-[44px]"
              title={t('warehouse.modules.warehouseTransfer.suggestions.selectLocation', {}, 'Velg som til-lokasjon')}
            >
              <MapPin className="h-4 w-4" />
              <span className="font-medium">{loc.locationName}</span>
              <span className="text-xs text-cyan-600">
                {t('warehouse.modules.warehouseTransfer.suggestions.balanceQty', { '0': loc.availableQuantity }, `saldo: ${loc.availableQuantity}`)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Tung variant: put-away-algoritmen (på forespørsel) */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <button
          onClick={runPutAway}
          disabled={loadingPutAway || !toWarehouse}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
            loadingPutAway || !toWarehouse
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-purple-500 text-white hover:bg-purple-600'
          }`}
        >
          {loadingPutAway ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {t('warehouse.modules.warehouseTransfer.suggestions.runPutAway', {}, 'Finn gode plasseringer')}
        </button>

        {putAwayCandidates !== null && (
          <div className="mt-3">
            {putAwayCandidates.length === 0 ? (
              <p className="text-sm text-gray-400">
                {t('warehouse.modules.warehouseTransfer.suggestions.noCandidates', {}, 'Ingen plasserings-kandidater funnet.')}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {putAwayCandidates.map((c, idx) => (
                  <button
                    key={`${c.binCode}|${idx}`}
                    onClick={() => onSelect(c.binCode, toWarehouse)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-800 transition-colors text-sm min-h-[44px]"
                    title={t('warehouse.modules.warehouseTransfer.suggestions.selectLocation', {}, 'Velg som til-lokasjon')}
                  >
                    <span className="flex items-center justify-center h-5 w-5 rounded-full bg-purple-200 text-purple-800 text-xs font-semibold">
                      {idx + 1}
                    </span>
                    <span className="font-medium">{c.binCode}</span>
                    <span className="text-xs text-purple-600">
                      {t('warehouse.modules.warehouseTransfer.suggestions.capacity', { '0': c.binQuantity, '1': c.maxQuantity }, `${c.binQuantity}/${c.maxQuantity}`)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
